import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { translate } from '../../../i18n'
import { useStore } from '../../../models'
import { ActionHint } from '../../../models/api-store'
import { DS } from '../../../design-system'
import { DSButtonType } from '../../../design-system/Button'
import React from 'react'

type ColorAndIcon = {
  icon: ReactElement
  type: DSButtonType
}

const ActionColorAndIcons: Partial<Record<ActionHint, ColorAndIcon>> = {
  ALLIN: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  BET: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  CALL: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  CHECK: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  FOLD: {
    icon: <DS.Icon name="Arrow--bottom-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  RAISE: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  }
}

interface PlayerActionsProps {
  actions: ActionHint[]
  onPlayerAction?: (action: ActionHint) => void
  imBack?: boolean
  showPreFold?: boolean
  threeCard?: boolean
}

export const PlayerActions = observer(
  ({ actions, onPlayerAction, imBack, showPreFold, threeCard }: PlayerActionsProps) => {
    const {
      game: {
        callAmount,
        minimumRaise,
        seatBackPlayerInGame,
        setHasUserPreFold,
        myPlayer,
        hasUserPreFold,
        isMyTurn,
        isGamePaused,
        isMyPlayerFoldedOrWaiting,
        isCardsVisibleOnShowDown,
        isMyPlayerObserver,
        isWalkthrough
      }
    } = useStore()
    const handleImBack = async () => await seatBackPlayerInGame(myPlayer?.userId)
    const handlePreFold = () => setHasUserPreFold(!hasUserPreFold)

    const getActions = () => {
      if ((isMyPlayerFoldedOrWaiting || isMyPlayerObserver) && !isWalkthrough) return null

      if (imBack) {
        return (
          <DS.Button variant="Large/Primary" type="On Light" onPress={handleImBack} maxWidth>
            {translate('teacherOptions.imBack')}
          </DS.Button>
        )
      }

      if (showPreFold) {
        const PreFoldDisabled = isMyTurn || isGamePaused || isCardsVisibleOnShowDown
        return (
          <DS.Button
            variant="Large/Primary"
            type="On Light"
            onPress={!PreFoldDisabled ? handlePreFold : undefined}
            rightIcon={<DS.Icon name="Arrow--bottom-right" size={16} color="Global/White" />}
            style={{ backgroundColor: DS.palette['Brand/Maroon'] }}
            centerText
          >
            {hasUserPreFold ? translate('playerActionHints.UNDOFOLD') : translate('playerActionHints.PREFOLD')}
          </DS.Button>
        )
      }

      const getActionText = (action: ActionHint) => {
        const text = translate(`newGameScreen.player.actionHints.${action}`)
        if (!isMyTurn) return text
        return text
      }

      const CallAmountIndicator = ({ action }: { action: ActionHint }) => (
        <TouchableOpacity onPress={() => onPlayerAction?.(ActionHint.CALL)} style={styles.callContainer}>
          <DS.Icon name="Chip" size={10} color="Global/Black" />
          <DS.Text variant="Caption/Bold" color="Brand/Green">
            {threeCard && [ActionHint.BET, ActionHint.RAISE].includes(action) ? minimumRaise : callAmount}
          </DS.Text>
        </TouchableOpacity>
      )

      const cannotFold = actions.length === 2 && !actions.includes(ActionHint.FOLD) && !isWalkthrough
      const newActions: ActionHint[] = [...actions, ...(cannotFold ? [ActionHint.FOLD] : [])]

      return (
        onPlayerAction &&
        newActions?.map((action, idx) => {
          const actionConfig = ActionColorAndIcons[action] || { type: 'On Light' }
          return (
            <React.Fragment key={`action-${action}-${idx}`}>
              <DS.Button
                numberOfLines={1}
                variant="Large/Primary"
                type={actionConfig.type}
                disabled={action === ActionHint.FOLD && cannotFold}
                onPress={() => onPlayerAction(action)}
                maxWidth
                rightIcon={ActionColorAndIcons[action]?.icon}
                style={[
                  action === ActionHint.FOLD ? { backgroundColor: DS.palette['Brand/Maroon'] } : {},
                  action === ActionHint.FOLD && cannotFold
                    ? { backgroundColor: DS.palette['Brand/Light Grey'], opacity: 1 }
                    : {}
                ]}
              >
                {getActionText(action)}
              </DS.Button>
              {action === ActionHint.CALL ? <CallAmountIndicator action={action} /> : null}
            </React.Fragment>
          )
        })
      )
    }
    return <View style={styles.container}>{getActions()}</View>
  }
)

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: DS.spacing[8],
    alignItems: 'center',
    width: '100%'
  },
  callContainer: {
    position: 'absolute',
    zIndex: 100,
    bottom: -12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 48,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White']
  }
})
